import { useState } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { encryptData, decryptData } from '../../shared/services/cryptoService';

interface CryptoState {
  encrypted: string;
  decrypted: string;
  error: string;
}

export default function CryptoTest() {
  const [input, setInput] = useState(
    '{"username":"testUser","password":"Test@123"}'
  );
  const [state, setState] = useState<CryptoState>({
    encrypted: '',
    decrypted: '',
    error: '',
  });

  const handleEncrypt = () => {
    try {
      const parsed = JSON.parse(input);
      const result = encryptData(parsed);
      setState({ encrypted: result.records, decrypted: '', error: '' });
    } catch {
      setState((prev) => ({
        ...prev,
        error: 'Invalid JSON — please enter valid JSON to encrypt.',
      }));
    }
  };

  const handleDecrypt = () => {
    if (!state.encrypted) {
      setState((prev) => ({
        ...prev,
        error: 'Nothing to decrypt — encrypt something first.',
      }));
      return;
    }
    try {
      const result = decryptData({ result: state.encrypted });
      setState((prev) => ({
        ...prev,
        decrypted: JSON.stringify(result, null, 2),
        error: '',
      }));
    } catch {
      setState((prev) => ({
        ...prev,
        error: 'Decryption failed — the encrypted string may be corrupted.',
      }));
    }
  };

  const handleReset = () => {
    setState({ encrypted: '', decrypted: '', error: '' });
  };

  return (
    <div className="container py-4">
      <h4 className="mb-1">Crypto Service — Encrypt / Decrypt Test</h4>

      <div className="row g-4">
        {/* Input */}
        <div className="col-md-4">
          <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column gap-3">
              <h6 className="card-title mb-0">1. Input JSON</h6>
              <InputTextarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                rows={20}
                className="w-100 font-monospace"
                style={{ fontSize: '0.8rem', resize: 'vertical' }}
                placeholder='{"key": "value"}'
              />
              <div className="d-flex gap-2">
                <Button
                  label="Encrypt"
                  icon="pi pi-lock"
                  onClick={handleEncrypt}
                  className="flex-fill"
                />
                <Button
                  label="Reset"
                  icon="pi pi-refresh"
                  onClick={handleReset}
                  severity="secondary"
                  outlined
                />
              </div>
            </div>
          </div>
        </div>

        {/* Encrypted output */}
        <div className="col-md-4">
          <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column gap-3">
              <h6 className="card-title mb-0">2. Encrypted Output</h6>
              <InputTextarea
                value={state.encrypted}
                readOnly
                rows={20}
                className="w-250 font-monospace"
                style={{
                  fontSize: '0.75rem',
                  background: '#f8f9fa',
                  resize: 'vertical',
                }}
                placeholder="Encrypted base64 string will appear here…"
              />
              <Button
                label="Decrypt"
                icon="pi pi-lock-open"
                onClick={handleDecrypt}
                severity="success"
                disabled={!state.encrypted}
              />
            </div>
          </div>
        </div>

        {/* Decrypted output */}
        <div className="col-md-4">
          <div className="card h-100 shadow-sm">
            <div className="card-body d-flex flex-column gap-3">
              <h6 className="card-title mb-0">3. Decrypted Output</h6>
              <InputTextarea
                value={state.decrypted}
                readOnly
                rows={20}
                className="w-250 font-monospace"
                style={{
                  fontSize: '0.8rem',
                  background: '#f8f9fa',
                  resize: 'vertical',
                }}
                placeholder="Decrypted JSON will appear here…"
              />
              {state.decrypted && (
                <div
                  className="alert alert-success py-2 mb-0"
                  style={{ fontSize: '0.8rem' }}
                >
                  <i className="pi pi-check-circle me-1" />
                  Round-trip successful — decrypted output matches original
                  input.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {state.error && (
        <div
          className="alert alert-danger mt-4 py-2"
          style={{ fontSize: '0.85rem' }}
        >
          <i className="pi pi-exclamation-triangle me-2" />
          {state.error}
        </div>
      )}
    </div>
  );
}
